<template>
  <div class="multi-select" ref="containerRef">
    <label v-if="label">{{ label }}</label>
    <div class="multi-select-wrapper">
      <div
        class="multi-select-trigger"
        :class="{ 'is-open': isOpen, 'has-selection': selectedItems.length > 0 }"
        @click="toggleDropdown"
      >
        <div class="selected-chips">
          <span
            v-for="item in selectedItems"
            :key="item.id"
            class="selected-chip"
            :style="{ backgroundColor: item.color || '#6B7280', color: '#fff' }"
            @click.stop="removeItem(item.id)"
          >
            {{ item.title }}
            <span class="chip-remove">×</span>
          </span>
          <span v-if="selectedItems.length === 0" class="placeholder">{{ placeholder }}</span>
        </div>
        <span class="dropdown-icon">{{ isOpen ? '▲' : '▼' }}</span>
      </div>

      <div v-if="isOpen" class="multi-select-dropdown" @click.stop>
        <div v-if="searchable" class="search-box">
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            :placeholder="searchPlaceholder"
            class="search-input"
            @input="handleSearch"
            @click.stop
          />
        </div>
        <div class="options-list" ref="optionsListRef" @click.stop>
          <div
            v-for="item in filteredOptions"
            :key="item.id"
            class="option-item"
            :class="{ 'is-selected': isSelected(item.id) }"
            :data-item-id="item.id"
            @click.stop="toggleItem(item)"
          >
            <span class="option-checkbox">{{ isSelected(item.id) ? '✓' : '' }}</span>
            <span
              class="option-badge"
              :style="{ backgroundColor: item.color || '#6B7280', color: '#fff' }"
            >
              {{ item.title }}
            </span>
          </div>
          <div v-if="filteredOptions.length === 0" class="no-results">
            {{ noResultsText }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'

const props = defineProps({
  options: {
    type: Array,
    required: true,
    default: () => [],
  },
  selectedIds: {
    type: Array,
    required: true,
    default: () => [],
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Select items...',
  },
  searchable: {
    type: Boolean,
    default: true,
  },
  searchPlaceholder: {
    type: String,
    default: 'Search...',
  },
  noResultsText: {
    type: String,
    default: 'No results found',
  },
})

const emit = defineEmits(['update:selectedIds'])

const isOpen = ref(false)
const searchQuery = ref('')
const containerRef = ref(null)
const searchInputRef = ref(null)
const optionsListRef = ref(null)

const selectedItems = computed(() => {
  return props.options.filter(option => props.selectedIds.includes(String(option.id)))
})

const filteredOptions = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.options
  }
  const query = searchQuery.value.toLowerCase().trim()
  return props.options.filter(option => option.title.toLowerCase().includes(query))
})

function isSelected(id) {
  return props.selectedIds.includes(String(id))
}

function toggleItem(item) {
  const itemId = String(item.id)
  const newSelectedIds = [...props.selectedIds]
  const index = newSelectedIds.indexOf(itemId)

  if (index > -1) {
    newSelectedIds.splice(index, 1)
  } else {
    newSelectedIds.push(itemId)
  }

  emit('update:selectedIds', newSelectedIds)
}

function removeItem(id) {
  const itemId = String(id)
  const newSelectedIds = props.selectedIds.filter(selectedId => selectedId !== itemId)
  emit('update:selectedIds', newSelectedIds)
}

function toggleDropdown() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    nextTick(() => {
      if (searchInputRef.value) {
        searchInputRef.value.focus()
      }
    })
  }
}

function handleSearch() {
  // Search is handled by computed property
}

function handleClickOutside(event) {
  if (!isOpen.value) return
  if (containerRef.value && containerRef.value.contains(event.target)) return
  // Also check if click is on the dropdown itself (which is positioned absolutely)
  const dropdown = containerRef.value?.querySelector('.multi-select-dropdown')
  if (dropdown && dropdown.contains(event.target)) return
  isOpen.value = false
}

watch(isOpen, newValue => {
  if (newValue) {
    // Use capture phase to catch clicks before they bubble
    document.addEventListener('click', handleClickOutside, true)
  } else {
    document.removeEventListener('click', handleClickOutside, true)
    searchQuery.value = ''
  }
})

onMounted(() => {
  // Don't add listener on mount if not open
  if (isOpen.value) {
    document.addEventListener('click', handleClickOutside, true)
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
})
</script>

<style scoped>
.multi-select {
  flex: 1;
}

.multi-select label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
}

.multi-select-wrapper {
  position: relative;
}

.multi-select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 38px;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}

.multi-select-trigger:hover {
  border-color: #adb5bd;
}

.multi-select-trigger.is-open {
  border-color: #1d6bf3;
  box-shadow: 0 0 0 3px rgba(29, 107, 243, 0.1);
}

.multi-select-trigger.has-selection {
  padding: 4px 10px;
}

.selected-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.2s;
}

.selected-chip:hover {
  opacity: 0.8;
}

.chip-remove {
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  opacity: 0.8;
}

.placeholder {
  color: #6c757d;
  font-size: 14px;
}

.dropdown-icon {
  color: #6c757d;
  font-size: 12px;
  margin-left: 8px;
  flex-shrink: 0;
}

.multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.search-box {
  padding: 8px;
  border-bottom: 1px solid #e9ecef;
}

.search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.search-input:focus {
  outline: none;
  border-color: #1d6bf3;
  box-shadow: 0 0 0 3px rgba(29, 107, 243, 0.1);
}

.options-list {
  overflow-y: auto;
  max-height: 250px;
  padding: 4px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.option-item:hover {
  background-color: #f8f9fa;
}

.option-item.is-selected {
  background-color: #e7f3ff;
}

.option-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 2px solid #ced4da;
  border-radius: 3px;
  font-size: 12px;
  color: #1d6bf3;
  flex-shrink: 0;
}

.option-item.is-selected .option-checkbox {
  background-color: #1d6bf3;
  border-color: #1d6bf3;
  color: white;
}

.option-badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
}

.no-results {
  padding: 16px;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
}
</style>
